import React, { useState, useEffect } from 'react';
import { Bell, Plus, X } from 'lucide-react';
import { K } from '../constants';

const BIG = { fontFamily: K.fSans, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1.1 };

const PRIORITY_STYLES = {
  urgent: { col: K.bad, label: 'Urgent' },
  important: { col: K.warn, label: 'Important' },
  normal: { col: K.teal, label: 'Notice' },
};

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function PostForm({ onPost, onClose }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [saving, setSaving] = useState(false);

  const handlePost = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onPost(title, body, priority);
      onClose();
    } catch (e) {
      console.error('Failed to post announcement:', e);
      setSaving(false);
    }
  };

  const inpSt = {
    background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8, color: K.t0,
    fontFamily: K.fSans, fontSize: 13, padding: '10px 12px',
    width: '100%', boxSizing: 'border-box', outline: 'none', fontWeight: 500,
  };

  return (
    <div style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t2, letterSpacing: 0.3, marginBottom: 10, fontWeight: 600 }}>New Announcement</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={inpSt} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message body (optional)" rows={3} style={{ ...inpSt, resize: 'vertical', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {Object.entries(PRIORITY_STYLES).map(([key, { col, label }]) => (
            <button key={key} onClick={() => setPriority(key)} style={{
              flex: 1, background: priority === key ? `${col}22` : K.bg2,
              border: `1px solid ${priority === key ? col : K.bdr}`, borderRadius: 8,
              padding: '7px 0', cursor: 'pointer', fontFamily: K.fSans, fontSize: 11, fontWeight: 600,
              color: priority === key ? col : K.t3, letterSpacing: 0.3,
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={handlePost} disabled={saving || !title.trim()} style={{
            flex: 1, background: K.teal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0',
            cursor: 'pointer', fontFamily: K.fSans, fontSize: 12, fontWeight: 600,
            letterSpacing: 0.3, opacity: saving ? 0.5 : 1,
          }}>{saving ? 'Posting...' : 'Post'}</button>
          <button onClick={onClose} style={{
            background: K.bg2, color: K.t2, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '10px 16px',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function Announcements({ announcements, loading, readIds, markRead, markAllRead, postAnnouncement, deleteAnnouncement, isAdmin, unreadCount }) {
  // Mark all as read when viewing
  useEffect(() => {
    if (announcements.length > 0 && unreadCount > 0) {
      markAllRead();
    }
  }, [announcements, unreadCount, markAllRead]);

  const [showPost, setShowPost] = useState(false);

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: K.t3 }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...BIG, fontSize: 28, color: K.teal }}>Announcements</div>
        <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t2, letterSpacing: 0.3, marginTop: 3, fontWeight: 500 }}>Department Notices</div>
      </div>

      {isAdmin && !showPost && (
        <button onClick={() => setShowPost(true)} style={{
          width: '100%', background: K.bg1, border: `1px dashed ${K.teal}44`, borderRadius: 9,
          padding: '10px 0', cursor: 'pointer', fontFamily: K.fSans, fontSize: 12,
          fontWeight: 600, color: K.teal, letterSpacing: 0.3, marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}><Plus size={16} color={K.teal} strokeWidth={1.75} />New Announcement</button>
      )}
      {isAdmin && showPost && <PostForm onPost={postAnnouncement} onClose={() => setShowPost(false)} />}

      {announcements.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: K.t3 }}>
          No announcements yet. {isAdmin ? 'Post one above.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {announcements.map((a) => {
            const p = PRIORITY_STYLES[a.priority] || PRIORITY_STYLES.normal;
            const isRead = readIds.has(a.id);
            return (
              <div key={a.id} style={{
                background: K.bg1, border: `1px solid ${K.bdr}`,
                borderLeft: `3px solid ${p.col}`,
                borderRadius: 12, padding: '12px 14px', boxShadow: K.sh1,
                opacity: isRead ? 0.85 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        fontFamily: K.fSans, fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
                        color: p.col, background: `${p.col}18`, padding: '2px 7px', borderRadius: 6,
                      }}>{p.label}</span>
                      <span style={{ fontFamily: K.fSans, fontSize: 10, color: K.t3, fontWeight: 500 }}>{timeAgo(a.createdAt)}</span>
                    </div>
                    <div style={{ fontFamily: K.fSans, fontSize: 15, fontWeight: 600, color: K.t0, marginBottom: 4 }}>{a.title}</div>
                    {a.body && <div style={{ fontSize: 12, color: K.t2, lineHeight: 1.6, fontWeight: 500 }}>{a.body}</div>}
                    <div style={{ fontSize: 10, color: K.t3, marginTop: 6, fontWeight: 500 }}>— {a.authorName}</div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => deleteAnnouncement(a.id)} style={{
                      background: 'none', border: 'none', color: K.t3, cursor: 'pointer',
                      padding: '4px 8px', opacity: 0.5, display: 'flex', alignItems: 'center',
                    }} aria-label="Delete"><X size={16} color={K.t3} strokeWidth={1.75} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Popup modal for first-time unread announcements */
export function AnnouncementPopup({ announcements, readIds, markAllRead, onView }) {
  const unread = announcements.filter((a) => !readIds.has(a.id));
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || unread.length === 0) return null;

  const handleDismiss = () => {
    markAllRead();
    setDismissed(true);
  };

  const latest = unread[0];
  const p = PRIORITY_STYLES[latest.priority] || PRIORITY_STYLES.normal;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 600,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, animation: 'fadeSlideIn 0.2s ease-out',
    }}>
      <div style={{
        background: K.bg1, border: `1px solid ${K.bdr}`, borderTop: `3px solid ${p.col}`,
        borderRadius: 12, padding: 20, maxWidth: 400, width: '100%', boxShadow: K.sh2,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{
            fontFamily: K.fSans, fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
            color: p.col, background: `${p.col}18`, padding: '2px 7px', borderRadius: 6,
          }}>{p.label}</span>
          {unread.length > 1 && (
            <span style={{ fontFamily: K.fSans, fontSize: 10, color: K.t3, fontWeight: 500 }}>+{unread.length - 1} more</span>
          )}
        </div>
        <div style={{ fontFamily: K.fSans, fontSize: 18, fontWeight: 700, color: K.t0, marginBottom: 6 }}>{latest.title}</div>
        {latest.body && <div style={{ fontSize: 13, color: K.t2, lineHeight: 1.6, marginBottom: 10, fontWeight: 500 }}>{latest.body}</div>}
        <div style={{ fontFamily: K.fSans, fontSize: 10, color: K.t3, marginBottom: 14, fontWeight: 500 }}>— {latest.authorName} · {timeAgo(latest.createdAt)}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {unread.length > 1 && (
            <button onClick={onView} style={{
              flex: 1, background: K.teal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0',
              cursor: 'pointer', fontFamily: K.fSans, fontSize: 12, fontWeight: 600,
              letterSpacing: 0.3,
            }}>View All ({unread.length})</button>
          )}
          <button onClick={handleDismiss} style={{
            flex: 1, background: K.bg2, color: K.t1, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '10px 0',
            cursor: 'pointer', fontFamily: K.fSans, fontSize: 12, fontWeight: 600,
            letterSpacing: 0.3,
          }}>Got it</button>
        </div>
      </div>
    </div>
  );
}

/* Floating announcements button */
export function AnnouncementFAB({ unreadCount, onClick }) {
  return (
    <button onClick={onClick} style={{
      position: 'fixed', bottom: 62, right: unreadCount > 0 ? 12 : 12, zIndex: 400,
      width: 44, height: 44, borderRadius: '50%',
      background: K.bg1, border: `1.5px solid ${unreadCount > 0 ? K.warn : K.teal}`,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: `0 4px 16px rgba(0,0,0,0.3)`,
    }}>
      <Bell size={20} color={unreadCount > 0 ? K.warn : K.teal} strokeWidth={1.75} />
      {unreadCount > 0 && (
        <div style={{
          position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18,
          borderRadius: 9, background: K.warn, color: '#f5f5f5',
          fontFamily: K.fMono, fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px', border: `2px solid ${K.bg}`,
        }}>{unreadCount}</div>
      )}
    </button>
  );
}
